import { useUserStore } from '@/store'

/**
 * 俱乐部邀请码归因工具
 *
 * 复用既有体系，不新增字段：
 * - 邀请码 = 俱乐部身份用户（商户/主理人）的对外 OID（ar_user.oid）
 * - 注册时作为 inviterId 提交，后端经「邀请人 → source_tenant_id」继承完成俱乐部归属
 * - 老用户（已登录）访问邀请链接直接忽略；邀请码无效时后端注册解析失败自动降级为平台来源，不阻断浏览
 *
 * 双缓存：localStorage（结构化带过期时间）+ cookie（兼容 webview / 跨页签），有效期 7 天。
 */

/** URL 查询参数名（与产品约定一致，禁止散落硬编码） */
export const INVITE_QUERY_KEY = 'invite_code'
const LS_KEY = 'aries_invite_code'
const COOKIE_KEY = 'aries_invite_code'
/** 邀请码有效期 7 天（毫秒 / 秒） */
const INVITE_TTL_MS = 7 * 24 * 60 * 60 * 1000
const INVITE_TTL_SEC = 7 * 24 * 60 * 60
/** 合法邀请码：数字ID 或字母数字中短线下划线（OID 形如 U1789xxxx），长度 4-64 */
const INVITE_CODE_REGEX = /^[A-Za-z0-9_-]{4,64}$/

interface InviteCache {
  code: string
  expireAt: number
}

function isValidCode(code: unknown): code is string {
  return typeof code === 'string' && INVITE_CODE_REGEX.test(code.trim())
}

function readCookie(name: string): string {
  const prefix = `${name}=`
  const item = document.cookie
    .split(';')
    .map((s) => s.trim())
    .find((s) => s.startsWith(prefix))
  return item ? decodeURIComponent(item.slice(prefix.length)) : ''
}

function writeCookie(code: string) {
  // SameSite=Lax 保障同站跳转携带；path=/ 全站可读
  document.cookie =
    `${COOKIE_KEY}=${encodeURIComponent(code)}; max-age=${INVITE_TTL_SEC}; path=/; SameSite=Lax`
}

function clearCookie() {
  document.cookie = `${COOKIE_KEY}=; max-age=0; path=/`
}

function readLocal(): InviteCache | null {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as Partial<InviteCache>
    if (!isValidCode(data.code) || typeof data.expireAt !== 'number') return null
    if (Date.now() > data.expireAt) return null
    return { code: data.code.trim(), expireAt: data.expireAt }
  } catch {
    return null
  }
}

function writeLocal(code: string) {
  const data: InviteCache = { code, expireAt: Date.now() + INVITE_TTL_MS }
  localStorage.setItem(LS_KEY, JSON.stringify(data))
}

/**
 * 在应用启动 / 路由切换时捕获链接中的邀请码。
 * 规则：已登录老用户直接忽略；仅当邀请码格式合法时双缓存 7 天。
 * 支持 hash 路由（#/path?invite_code=x）与普通 query（?invite_code=x）两种位置。
 */
export function captureInviteCode(): void {
  try {
    const userStore = useUserStore()
    if (userStore.isLogin) return

    const params = new URLSearchParams(window.location.search || '')
    let code = params.get(INVITE_QUERY_KEY) || ''
    if (!code && window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.slice(window.location.hash.indexOf('?') + 1)
      code = new URLSearchParams(hashQuery).get(INVITE_QUERY_KEY) || ''
    }
    code = (code || '').trim()
    if (!isValidCode(code)) return

    writeLocal(code)
    writeCookie(code)
  } catch (e) {
    // 隐私模式 / 存储被禁用等场景静默降级，不影响浏览
    console.warn('[invite] 邀请码缓存失败', e)
  }
}

/**
 * 读取有效邀请码（注册时调用）：优先 localStorage，回退 cookie；过期/非法返回 null 并清理。
 */
export function getInviteCode(): string | null {
  const local = readLocal()
  if (local?.code) return local.code

  const cookieVal = readCookie(COOKIE_KEY)
  if (isValidCode(cookieVal)) {
    writeLocal(cookieVal.trim())
    return cookieVal.trim()
  }
  if (cookieVal) clearInviteCode()
  return null
}

/** 注册成功后清理邀请码缓存（归属关系已落库，无需继续携带） */
export function clearInviteCode(): void {
  try {
    localStorage.removeItem(LS_KEY)
  } catch { /* ignore */ }
  clearCookie()
}

/**
 * 为分享链接拼接当前俱乐部身份用户的邀请码。
 * - 仅 MERCHANT（商户/主理人）账号携带；普通用户返回原链接
 * - 已携带 invite_code 时不覆盖；兼容 hash 路由（#/path?a=1）
 */
export function buildInviteLink(rawUrl: string, inviteCode?: string): string {
  try {
    const code = (inviteCode || '').trim()
    if (!code) return rawUrl

    const url = new URL(rawUrl, window.location.origin)
    if (url.hash) {
      // hash 路由参数在 # 之后，需合并到 hash 的 query 上
      if (url.hash.includes('?')) {
        const qIndex = url.hash.indexOf('?')
        const path = url.hash.slice(0, qIndex)
        const hashParams = new URLSearchParams(url.hash.slice(qIndex + 1))
        if (!hashParams.get(INVITE_QUERY_KEY)) hashParams.set(INVITE_QUERY_KEY, code)
        url.hash = `${path}?${hashParams.toString()}`
      } else {
        url.hash = `${url.hash}?${INVITE_QUERY_KEY}=${encodeURIComponent(code)}`
      }
    } else if (!url.searchParams.get(INVITE_QUERY_KEY)) {
      url.searchParams.set(INVITE_QUERY_KEY, code)
    }
    return url.toString()
  } catch {
    return rawUrl
  }
}

/* ==========================================================================
 * 俱乐部推广链接归因（后管「商户管理-链接」按俱乐部生成，投放到公众号菜单）
 *
 * 与上方 invite_code（主理人 OID）相互独立：
 * - ctk 为服务端 HMAC 签名的紧凑串（CT1. 前缀，约 19 字符），载荷=俱乐部租户ID
 * - 游客打开链接时双缓存 7 天；注册时随注册请求提交（payload.clubToken），后端自动入社
 * - 已登录用户不捕获；缓存键与 invite_code / invite_token 互不覆盖
 * ========================================================================== */

/** URL 查询参数名（与后端 TenantConstant.CLUB_TOKEN_QUERY_KEY 约定一致，短参数名） */
export const CLUB_TOKEN_QUERY_KEY = 'ctk'
const LS_CLUB_KEY = 'aries_club_token'
const COOKIE_CLUB_KEY = 'aries_club_token'
/** 服务端 token：CT1.体.签名，base64url（字母数字 _ - .），整体不超过 40 字符 */
const CLUB_TOKEN_REGEX = /^CT1\.[A-Za-z0-9_.-]{4,40}$/

interface ClubTokenCache {
  token: string
  expireAt: number
}

function isValidClubToken(v: unknown): v is string {
  return typeof v === 'string' && CLUB_TOKEN_REGEX.test(v.trim())
}

function readClubTokenLocal(): ClubTokenCache | null {
  try {
    const raw = localStorage.getItem(LS_CLUB_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as Partial<ClubTokenCache>
    if (!isValidClubToken(data.token) || typeof data.expireAt !== 'number') return null
    if (Date.now() > data.expireAt) return null
    return { token: data.token.trim(), expireAt: data.expireAt }
  } catch {
    return null
  }
}

/**
 * 应用启动 / 路由切换时捕获链接中的 ctk（hash query 与普通 query 均支持）。
 * 已登录用户忽略；非法格式忽略（无效 token 后端也会直接丢弃，不影响注册/浏览）。
 */
export function captureClubToken(): void {
  try {
    const userStore = useUserStore()
    if (userStore.isLogin) return

    const params = new URLSearchParams(window.location.search || '')
    let token = params.get(CLUB_TOKEN_QUERY_KEY) || ''
    if (!token && window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.slice(window.location.hash.indexOf('?') + 1)
      token = new URLSearchParams(hashQuery).get(CLUB_TOKEN_QUERY_KEY) || ''
    }
    token = (token || '').trim()
    if (!isValidClubToken(token)) return

    localStorage.setItem(LS_CLUB_KEY, JSON.stringify({ token, expireAt: Date.now() + INVITE_TTL_MS }))
    document.cookie =
      `${COOKIE_CLUB_KEY}=${encodeURIComponent(token)}; max-age=${INVITE_TTL_SEC}; path=/; SameSite=Lax`
  } catch (e) {
    // 隐私模式 / 存储被禁用静默降级，不影响浏览
    console.warn('[invite] 俱乐部 token 缓存失败', e)
  }
}

/** 注册时读取有效俱乐部 token；过期/非法返回 null 并清理 */
export function getClubToken(): string | null {
  const local = readClubTokenLocal()
  if (local?.token) return local.token
  const cookieVal = readCookie(COOKIE_CLUB_KEY)
  if (isValidClubToken(cookieVal)) {
    localStorage.setItem(
      LS_CLUB_KEY,
      JSON.stringify({ token: cookieVal.trim(), expireAt: Date.now() + INVITE_TTL_MS })
    )
    return cookieVal.trim()
  }
  if (cookieVal) clearClubToken()
  return null
}

/** 注册成功后清除（归属关系已落库，无需继续携带） */
export function clearClubToken(): void {
  try {
    localStorage.removeItem(LS_CLUB_KEY)
  } catch { /* ignore */ }
  document.cookie = `${COOKIE_CLUB_KEY}=; max-age=0; path=/`
}
